// Concept-drawer-local constants. Keep magic strings/objects here so
// section files stay declarative and easy to tweak in one place.

/* Drawer / header dimensions. */
export const CONCEPT_DRAWER_SHEET_CLS =
  "z-[80] flex h-full w-[1400px] max-w-[95vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]";
export const CONCEPT_DRAWER_OVERLAY_CLS = "z-[80]";
export const CONCEPT_HEADER_AVG_MIN_WIDTH = "min-w-[130px]";

/* Copy strings surfaced by the drawer shell. */
export const CONCEPT_DRAWER_COPY = {
  title: "Concept Intelligence",
  description:
    "Key concepts and recurring themes identified from the document, with context, provenance, and confidence.",
  loading: "Loading concept intelligence...",
} as const;

/* Recurring-themes row visuals (hoisted out of recurringThemesSection.tsx). */
export const RECURRING_PILL_CLS =
  "inline-flex items-center rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-violet-700 border border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/25 whitespace-nowrap";
export const RECURRING_BAR_CLS =
  "absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 to-fuchsia-500";
export const RECURRING_UNLINKED_FLAG = "not_enabled";
export const RECURRING_UNLINKED_SUFFIX = " · unlinked";

/* Provenance row visual. */
export const PROVENANCE_BAR_CLS =
  "absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 to-cyan-500";

/* Coverage bar (defined-terms table). */
export const COVERAGE_BAR_CLS =
  "absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 to-teal-500";

/* Filter chip visuals — used by conceptSectionFilterChip. */
export const SECTION_FILTER_ACTIVE_CLS =
  "bg-indigo-100 border-indigo-200 text-indigo-700 dark:bg-indigo-500/15 dark:border-indigo-500/25 dark:text-indigo-300";
export const SECTION_FILTER_INACTIVE_CLS =
  "bg-background border-border text-muted-foreground hover:text-foreground";

/* Search input focus ring for the defined-term search box. */
export const DEFINED_SEARCH_INPUT_CLS =
  "h-auto border-border bg-background pl-8 pr-8 py-1.5 text-[12.5px] text-foreground placeholder:text-muted-foreground focus-visible:border-indigo-500 focus-visible:ring-indigo-500/30";

/* ─── Concept Groups (group→concept flow) ───────────────────── */

/** Copy strings for the Concept Groups section. */
export const CONCEPT_GROUPS_COPY = {
  header: "Concept Groups",
  emptyText: "No concepts match this search or source filter.",
  ungroupedLabel: "Unclassified",
  ungroupedDescription:
    "Concepts extracted but not confidently placed in any auto-discovered group.",
  searchPlaceholder: "Search",
  sourceFilterLabel: "Source",
  belongsToPrefix: "Belongs to",
} as const;

/** Source-filter chip labels. */
export const CONCEPT_SOURCE_FILTER_LABELS = {
  all: "All",
  defined: "Defined",
  referenced: "Referenced",
} as const;

/** Group card wrapper. */
export const GROUP_CARD_CLS =
  "rounded-xl border border-border bg-card overflow-hidden";
export const GROUP_HEADER_CLS =
  "flex items-start justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3";
export const GROUP_HEADER_UNCLASSIFIED_CLS =
  "flex items-start justify-between gap-3 border-b border-border bg-amber-50 dark:bg-amber-500/10 px-4 py-3";

/** Group name + description. */
export const GROUP_NAME_CLS =
  "text-[13.5px] font-bold text-foreground break-words tracking-tight";
export const GROUP_DESCRIPTION_CLS =
  "mt-0.5 text-[11.5px] text-muted-foreground break-words leading-snug";

/** Group meta counters (defined/referenced/occurrences pills). */
export const GROUP_META_PILL_CLS =
  "inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground whitespace-nowrap";
export const GROUP_META_STRONG_CLS =
  "inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10.5px] font-bold text-indigo-700 border border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/25 whitespace-nowrap";

/** Concept row inside a group card. */
export const CONCEPT_ROW_CLS =
  "grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-border/50 last:border-b-0 px-4 py-3 hover:bg-muted/20 transition-colors";
export const CONCEPT_ROW_META_CLS =
  "flex flex-wrap items-center gap-1.5 mt-1 text-[10.5px] text-muted-foreground";

/** Source badges. */
export const SOURCE_BADGE_DEFINED_CLS =
  "inline-flex items-center rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25 whitespace-nowrap";
export const SOURCE_BADGE_REFERENCED_CLS =
  "inline-flex items-center rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-violet-700 border border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/25 whitespace-nowrap";

/** View mode (accordion cards vs flat table). */
export const CONCEPT_VIEW_MODE = {
  ACCORDION: "accordion",
  TABLE: "table",
} as const;
export type ConceptViewMode =
  (typeof CONCEPT_VIEW_MODE)[keyof typeof CONCEPT_VIEW_MODE];

export const CONCEPT_VIEW_MODE_LABELS: Record<ConceptViewMode, string> = {
  accordion: "Cards",
  table: "Table",
};

export const CONCEPT_VIEW_MODE_ORDER: readonly ConceptViewMode[] = [
  CONCEPT_VIEW_MODE.ACCORDION,
  CONCEPT_VIEW_MODE.TABLE,
] as const;

/** Accordion header button — full-width clickable row, no default btn chrome. */
export const GROUP_HEADER_BUTTON_CLS =
  "w-full h-auto text-left justify-start whitespace-normal p-0 hover:bg-transparent";

/** DataTable geometry for the table view. */
export const CONCEPT_TABLE_MAX_HEIGHT = "520px";
export const CONCEPT_TABLE_PAGE_SIZE = 10;
export const CONCEPT_VIEW_TOGGLE_LABEL = "View";
